import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useOfferAffiliates } from "@/hooks/use-offers";
import { StatusBadge } from "@/components/StatusBadge";
import { Plus, Trash2, Filter, Pencil, X, Check } from "lucide-react";

interface ScrubRate {
  id: number;
  affiliateId: number;
  affiliateName: string;
  scrubRatePercentage: number;
  status: string;
  targetCountries: string | null;
  targetPlatforms: string | null;
  targetDeviceTypes: string | null;
  createdAt: string;
}

const inputCls = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900";
const labelCls = "block text-sm font-medium text-gray-700 mb-1";

export function OfferScrubRatesTab({ offerId }: { offerId: string }) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["offers", offerId, "custom-scrub-rates"],
    queryFn: async () => {
      const res = await api.get(`/v1/offers/${offerId}/custom-scrub-rates`);
      if (!res.ok) throw new Error("Failed to fetch scrub rates");
      return res.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (rateId: number) => {
      const res = await api.delete(`/v1/offers/${offerId}/custom-scrub-rates/${rateId}`);
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["offers", offerId, "custom-scrub-rates"] }),
  });

  const rates: ScrubRate[] = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-700">{rates.length} custom scrub rate{rates.length !== 1 ? "s" : ""}</h3>
        <button onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-800">
          <Plus className="w-4 h-4" /> Add Scrub Rate
        </button>
      </div>

      {isLoading ? (
        <div className="h-32 bg-gray-100 rounded-xl animate-pulse" />
      ) : rates.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-xl border border-gray-200">
          <Filter className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No per-affiliate scrub rates set. The global offer scrub rate applies.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Affiliate</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Scrub Rate</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Targeting</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rates.map((r) => {
                const countries = r.targetCountries ? JSON.parse(r.targetCountries) : [];
                const platforms = r.targetPlatforms ? JSON.parse(r.targetPlatforms) : [];
                const devices = r.targetDeviceTypes ? JSON.parse(r.targetDeviceTypes) : [];
                const hasTargeting = countries.length > 0 || platforms.length > 0 || devices.length > 0;
                return (
                  <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900">{r.affiliateName}</span>
                      <span className="text-xs text-gray-400 ml-1">#{r.affiliateId}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-semibold text-gray-900">{r.scrubRatePercentage}%</span>
                    </td>
                    <td className="px-4 py-3">
                      {hasTargeting ? (
                        <div className="text-xs text-gray-500 space-y-0.5">
                          {countries.length > 0 && <div>GEO: {countries.join(", ")}</div>}
                          {platforms.length > 0 && <div>OS: {platforms.join(", ")}</div>}
                          {devices.length > 0 && <div>Dev: {devices.join(", ")}</div>}
                        </div>
                      ) : <span className="text-xs text-gray-400">Any</span>}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setEditingId(r.id)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => { if (window.confirm("Delete?")) deleteMutation.mutate(r.id); }}
                          className="p-1.5 text-red-400 hover:text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && <ScrubRateFormModal offerId={offerId} onClose={() => setShowForm(false)} />}
      {editingId !== null && <EditScrubRateModal offerId={offerId} rate={rates.find((r) => r.id === editingId)!} onClose={() => setEditingId(null)} />}
    </div>
  );
}

function ScrubRateFormModal({ offerId, onClose }: { offerId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { data: affData } = useOfferAffiliates(offerId);
  const assignedAffiliates = affData?.data ?? [];

  const [affiliateId, setAffiliateId] = useState("");
  const [percentage, setPercentage] = useState("0");
  const [status, setStatus] = useState("active");
  const [targetCountries, setTargetCountries] = useState("");
  const [targetPlatforms, setTargetPlatforms] = useState("");
  const [targetDeviceTypes, setTargetDeviceTypes] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload: Record<string, unknown> = {
        affiliateId: Number(affiliateId),
        scrubRatePercentage: Number(percentage),
        status,
      };
      if (targetCountries.trim()) payload.targetCountries = targetCountries.split(",").map((s: string) => s.trim()).filter(Boolean);
      if (targetPlatforms.trim()) payload.targetPlatforms = targetPlatforms.split(",").map((s: string) => s.trim()).filter(Boolean);
      if (targetDeviceTypes.trim()) payload.targetDeviceTypes = targetDeviceTypes.split(",").map((s: string) => s.trim()).filter(Boolean);

      const res = await api.post(`/v1/offers/${offerId}/custom-scrub-rates`, payload);
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || "Failed"); }
      queryClient.invalidateQueries({ queryKey: ["offers", offerId, "custom-scrub-rates"] });
      onClose();
    } catch (err: any) { setError(err.message); } finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Add Custom Scrub Rate</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">{error}</div>}
          <div>
            <label className={labelCls}>Affiliate *</label>
            <select required value={affiliateId} onChange={(e) => setAffiliateId(e.target.value)} className={inputCls}>
              <option value="">Select affiliate...</option>
              {assignedAffiliates.map((a: any) => <option key={a.affiliateId} value={a.affiliateId}>{a.affiliateName} #{a.affiliateId}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Scrub Rate (%)</label>
              <input type="number" min="0" max="100" step="0.1" value={percentage} onChange={(e) => setPercentage(e.target.value)} className={inputCls} />
              <p className="text-xs text-gray-400 mt-1">Percentage of conversions to reject.</p>
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Targeting (optional)</h4>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Countries</label>
                <input type="text" value={targetCountries} onChange={(e) => setTargetCountries(e.target.value)} className={inputCls} placeholder="US,CA" />
              </div>
              <div>
                <label className={labelCls}>Platforms</label>
                <input type="text" value={targetPlatforms} onChange={(e) => setTargetPlatforms(e.target.value)} className={inputCls} placeholder="iOS,Android" />
              </div>
              <div>
                <label className={labelCls}>Devices</label>
                <input type="text" value={targetDeviceTypes} onChange={(e) => setTargetDeviceTypes(e.target.value)} className={inputCls} placeholder="mobile,desktop" />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
              {loading ? "Saving..." : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditScrubRateModal({ offerId, rate, onClose }: { offerId: string; rate: ScrubRate; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [percentage, setPercentage] = useState(String(rate.scrubRatePercentage));
  const [status, setStatus] = useState(rate.status);
  const [targetCountries, setTargetCountries] = useState(() => {
    try { return rate.targetCountries ? JSON.parse(rate.targetCountries).join(", ") : ""; } catch { return ""; }
  });
  const [targetPlatforms, setTargetPlatforms] = useState(() => {
    try { return rate.targetPlatforms ? JSON.parse(rate.targetPlatforms).join(", ") : ""; } catch { return ""; }
  });
  const [targetDeviceTypes, setTargetDeviceTypes] = useState(() => {
    try { return rate.targetDeviceTypes ? JSON.parse(rate.targetDeviceTypes).join(", ") : ""; } catch { return ""; }
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload: Record<string, unknown> = {
        scrubRatePercentage: Number(percentage),
        status,
        targetCountries: targetCountries.trim() ? targetCountries.split(",").map((s: string) => s.trim()).filter(Boolean) : null,
        targetPlatforms: targetPlatforms.trim() ? targetPlatforms.split(",").map((s: string) => s.trim()).filter(Boolean) : null,
        targetDeviceTypes: targetDeviceTypes.trim() ? targetDeviceTypes.split(",").map((s: string) => s.trim()).filter(Boolean) : null,
      };
      const res = await api.patch(`/v1/offers/${offerId}/custom-scrub-rates/${rate.id}`, payload);
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || "Failed to update"); }
      queryClient.invalidateQueries({ queryKey: ["offers", offerId, "custom-scrub-rates"] });
      onClose();
    } catch (err: any) { setError(err.message); } finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Edit Scrub Rate — {rate.affiliateName}</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">{error}</div>}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Scrub Rate (%)</label>
              <input type="number" min="0" max="100" step="0.1" value={percentage} onChange={(e) => setPercentage(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Targeting</h4>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Countries</label>
                <input type="text" value={targetCountries} onChange={(e) => setTargetCountries(e.target.value)} className={inputCls} placeholder="US,CA" />
              </div>
              <div>
                <label className={labelCls}>Platforms</label>
                <input type="text" value={targetPlatforms} onChange={(e) => setTargetPlatforms(e.target.value)} className={inputCls} placeholder="iOS,Android" />
              </div>
              <div>
                <label className={labelCls}>Devices</label>
                <input type="text" value={targetDeviceTypes} onChange={(e) => setTargetDeviceTypes(e.target.value)} className={inputCls} placeholder="mobile,desktop" />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
              {loading ? "Saving..." : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
